import { useEffect, useState } from 'react';
import useStorage from '@/hooks/useStorage';

/** Apple Pay JS version. v3 is what every browser that supports Apple Pay on the web provides. */
export const APPLE_PAY_VERSION = 3;

/**
 * The only status where Apple tells us not to draw a button at all. The other three all warrant
 * showing it: the customer either has a credential, might have one, or can add one inside the
 * sheet as part of the purchase.
 */
const APPLE_PAY_UNSUPPORTED: ApplePayJS.PaymentCredentialStatus = 'applePayUnsupported';

/**
 * Whether Apple Pay can actually complete a payment here.
 *
 * `ApplePaySession` existing proves nothing: Apple's SDK (loaded in index.html for the
 * <apple-pay-button> element) polyfills it on non-Safari browsers to offer the cross-browser
 * flow, and that polyfill's `supportsVersion` returns true unconditionally while
 * `canMakePaymentsWithActiveCard` just delegates to `canMakePayments`. Only
 * `applePayCapabilities()` reports whether the device supports Apple Pay at all — on Safari it
 * reflects the wallet, and on Chrome it reflects whether the hand-off to a nearby device is
 * available.
 *
 * The caller must gate the whole payment-method card on this, not just the button: a card with
 * no working button behind it looks available and does nothing when tapped.
 */
export default function useIsApplePayAvailable() {
  const storage = useStorage();
  const { iQ11TokenInfo } = storage.token.getIQ11Token();
  const merchantIdentifier = iQ11TokenInfo?.ApplePayMerchantIdentifier;
  const [isAvailable, setIsAvailable] = useState(false);

  useEffect(() => {
    if (!merchantIdentifier) return;

    if (typeof window.ApplePaySession === 'undefined' || !ApplePaySession.supportsVersion(APPLE_PAY_VERSION)) {
      return;
    }

    // applePayCapabilities is a recent addition. Older Safari has a perfectly working Apple Pay
    // without it, and calling it there throws synchronously — which would escape this effect and
    // take down the whole payment-method page, not just this card. Fall back to canMakePayments(),
    // which is what that generation of Safari answers with.
    if (typeof ApplePaySession.applePayCapabilities !== 'function') {
      setIsAvailable(ApplePaySession.canMakePayments());

      return;
    }

    let cancelled = false;

    try {
      ApplePaySession.applePayCapabilities(merchantIdentifier)
        .then(({ paymentCredentialStatus }) => {
          if (!cancelled) setIsAvailable(paymentCredentialStatus !== APPLE_PAY_UNSUPPORTED);
        })
        .catch((error) => {
          console.error(error);
          if (!cancelled) setIsAvailable(false);
        });
    } catch (error) {
      // Nothing about this card is worth breaking the page for.
      console.error(error);
      setIsAvailable(false);
    }

    return () => {
      cancelled = true;
    };
  }, [merchantIdentifier]);

  return isAvailable;
}
